import { t } from '@core/i18n';
import { CurrencyRange, MetricRange } from '@shared/types/common/range';

export const normalizedHTMLText = (text?: string) => {
  if (!text) return '';

  return text.replace(/\n/g, '<br />');
};

export const normalizedHTMLTextWithSpaces = (text?: string) => {
  if (!text) return '';

  // Replace each space with an HTML entity &nbsp; also a line break replace on the HTML <br /> tag
  return text.replace(/ /g, '&nbsp;').replace(/\n/g, '<br />');
};

export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const decodeHTMLEntities = (text?: string) => {
  if (!text) return '';

  const parser = new DOMParser();
  const doc = parser.parseFromString(text, 'text/html');
  return doc.documentElement.textContent || '';
};

export const getMetricString = (metric: Metric) => {
  // TODO: Refactor so that we supply config as an argument vs. referencing window.AS
  // TODO: We will need to make sure that we have separated utils, e.g. one for rent / size / etc.

  switch (metric) {
    case 'sqft':
      return t('sq_ft');

    case 'sqm':
    case 'sqm_month':
      return t(window?.AS?.constants.localisation?.use_sqm_symbol ? 'sq_m_1' : 'sq_m');

    case 'acre':
    case 'acres':
      return t('acres');

    case 'desk':
    case 'desks':
      return t('desks');

    case 'hectare':
    case 'hectares':
      return t('hectares');

    case 'annum':
      return t('annum');

    case 'month':
      return t('month');

    case 'bed':
    case 'beds':
      return t('beds');

    default:
      return metric ?? '';
  }
};

export const formatValueMetric = (value: string | number, metric: Metric) => {
  if (!value) {
    return '';
  }

  return value.toString() + ' / ' + getMetricString(metric);
};

export const copyToClipboard = (str: string) => {
  const el = document.createElement('textarea');
  el.value = str;
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
};

export const getAddressHtml = (line1 = '', line2 = '', town = '', county = '', postcode = '', country = '') => {
  const addressString = [line1, line2, town, county, postcode, country].filter((value) => value).join(',\n');

  return addressString ? normalizedHTMLText(addressString) : '-';
};

export const transformTextToSlug = (text: string) => {
  return text
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '');
};

export const getRangeMetricString = (data: MetricRange): string => {
  if (data.from && data.to) {
    if (data.from === data.to) {
      return `${data.from} ${getMetricString(data.metric)}`;
    } else {
      return t('xinputminx_to_xinputmaxx_xinputmetricx', {
        input_min: data.from,
        input_max: data.to,
        input_metric: getMetricString(data.metric),
      });
    }
  }

  if (data.from && !data.to) {
    return `${data.from} ${getMetricString(data.metric)}`;
  } else if (!data.from && data.to) {
    return `${data.to} ${getMetricString(data.metric)}`;
  }

  return '';
};
export const getPriceCurrencyString = (data: CurrencyRange, currencySymbol: string): string => {
  if (data.from && data.to) {
    if (data.from === data.to) {
      return `${data.from} ${currencySymbol}`;
    } else {
      return t('xinputminx_to_xinputmaxx_xinputmetricx', {
        input_min: data.from,
        input_max: data.to,
        input_metric: currencySymbol,
      });
    }
  }

  if (data.from && !data.to) {
    return `${data.from} ${currencySymbol}`;
  } else if (!data.from && data.to) {
    return `${data.to} ${currencySymbol}`;
  }

  return '';
};

export const toSentenceCase = (text: string) => {
  return text.toLowerCase().replace(/(^\w|\.\s+\w|\?\s+\w|!\s+\w)/g, (match) => match.toUpperCase());
};

export const toTitleCase = (text: string) => {
  return text
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const getFullName = (forename: string, surname: string) => {
  return [forename, surname].filter(Boolean).join(' ');
};

export const getTitleName = (titleLabel: string, name: string) => {
  return titleLabel ? `${titleLabel} ${name}` : name;
};

export const getNameCompany = (name: string, companyName?: string) => {
  if (companyName) {
    return t('xuserx_bracket_xorganisationx_bracket', { user: name, organisation: companyName });
  }
  return name;
};
